/**
 * Sending an object's bytes straight to storage, with how far it has got
 * observable while it is happening.
 *
 * This is `XMLHttpRequest` in 2026 on purpose. `fetch` reports nothing about a
 * request body in flight and has no event that could: the promise settles once
 * the response arrives and there is nothing in between. `xhr.upload` fires
 * `progress` with the bytes sent, which is the only way a browser will say so
 * without a `ReadableStream` body, and that path is unsupported on the requests
 * that matter here. The size this is for is the reason it matters: the bytes go
 * to a signed URL rather than through the API so an instance is not a proxy for
 * a 40 GB master (ADR 000028), and an upload of that size is measured in hours.
 *
 * Nothing here knows about assets. It takes a URL and a blob, so the route that
 * owns the asset record owns what to do when this fails.
 */

/** Thrown when the caller's signal aborted, rather than storage refusing. */
export class UploadCancelled extends Error {
  constructor() {
    super("cancelled");
    this.name = "UploadCancelled";
  }
}

/** How far an upload has got, as the browser reports it. */
export interface UploadProgress {
  /** Bytes acknowledged as sent. */
  sent: number;
  /** Bytes in total, which is the blob's own size. */
  total: number;
}

export interface PutObjectOptions {
  /** The signed URL to PUT to. */
  url: string;
  /** The bytes. */
  body: Blob;
  /** What storage should record the object as. */
  contentType: string;
  /** Called as the bytes go, and once at the end with sent === total. */
  onProgress?: (progress: UploadProgress) => void;
  /** Aborts the request, which rejects with {@link UploadCancelled}. */
  signal?: AbortSignal;
}

/**
 * PUTs a blob and resolves when storage has accepted it.
 *
 * Rejects with {@link UploadCancelled} if the signal aborted, and with an
 * ordinary `Error` otherwise: a refusal names the status, a dead connection
 * says so. The distinction is the caller's to act on, because a deliberate
 * cancel and a broken pipe are not the same event even though both leave an
 * asset record with no bytes behind it (ADR 000083).
 */
export function putObject(options: PutObjectOptions): Promise<void> {
  const { url, body, contentType, onProgress, signal } = options;

  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new UploadCancelled());
      return;
    }

    const xhr = new XMLHttpRequest();
    // The listener is removed on every exit below. Left attached it holds this
    // request, and its blob, for as long as the caller holds the controller.
    const abort = () => xhr.abort();
    const settle = (finish: () => void) => {
      signal?.removeEventListener("abort", abort);
      finish();
    };

    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        // lengthComputable is false where the browser cannot tell the length,
        // and the blob knows it regardless, so the total never goes missing
        // mid-upload and leaves a percentage that cannot be worked out.
        onProgress({
          sent: event.loaded,
          total: event.lengthComputable ? event.total : body.size,
        });
      };
    }

    xhr.onload = () =>
      settle(() => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // The last progress event fires before the response, but a small
          // enough object can be sent in one go with none at all, so the row
          // is told it is complete here rather than inferring it from silence.
          onProgress?.({ sent: body.size, total: body.size });
          resolve();
          return;
        }
        reject(new Error(`storage answered ${xhr.status}`));
      });

    xhr.onerror = () => settle(() => reject(new Error("the connection to storage failed")));
    xhr.ontimeout = () => settle(() => reject(new Error("storage stopped answering")));
    xhr.onabort = () => settle(() => reject(new UploadCancelled()));

    signal?.addEventListener("abort", abort, { once: true });

    xhr.send(body);
  });
}
